import React, { useEffect, useState } from 'react';
import './Footer.scss';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { productsActions } from '../../store/products';
import { UiActions } from '../../store/ui';
import { Button, EButtonType, parseToApiDate } from '@repo/apg-ui';
import { SearchLocation } from '../../store/search';
import { generateCriteria } from '../../utils/payloads';
import { SearchParameters } from '../../store/search/types';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';

interface Props {
  batchEditFailedCount: number;
  productFiltersByUrn: any;
  isShowBottomBar: boolean;
  searchLocations: SearchLocation[];
  actions: typeof productsActions;
  uiActions: typeof UiActions;
  searchParameters: SearchParameters;
  notices: any;
}

const Footer = ({
  productFiltersByUrn,
  isShowBottomBar,
  actions,
  uiActions,
  searchLocations,
  searchParameters,
  batchEditFailedCount,
  notices,
}: Props) => {
  const [toastId, setToastId] = useState<any>(null);
  const intl = useIntl();

  useEffect(() => {
    if (batchEditFailedCount > 0) {
      showToast();
    } else {
      setToastId(null);
      toast.dismiss();
    }
  }, [batchEditFailedCount]);

  const showToast = () => {
    const message = intl.formatMessage(
      {
        id: 'ec.general.products.batchEditFailed',
        defaultMessage: '{count} Produkte erhalten keinen gültigen Preis für die gegebene Konfiguration.',
      },
      { count: batchEditFailedCount },
    );

    if (toastId) {
      toast.update(toastId, { render: message });
    } else {
      const toastData = toast.warning(message, {
        progress: 0,
        closeOnClick: false,
        onClose: () => {
          setToastId(null);
          actions.updateBatchEditFailedCount(0);
        },
      });
      setToastId(toastData);
    }
  };

  const updateProductPrices = () => {
    actions.updateBatchEditFailedCount(0);
    for (const productUrn of Object.keys(productFiltersByUrn)) {
      const productFilter = productFiltersByUrn[productUrn];
      const startDate = productFilter.dateRanges?.[0].startDate || searchParameters.startDate;
      let payload = {
        producturn: productUrn,
      };
      if (productFilter.budget) {
        payload['budget'] = productFilter.budget;
      }
      if (productFilter.dateRanges?.length > 0) {
        payload['duration'] = productFilter.duration;
        payload['startdate'] = parseToApiDate(startDate);
      } else {
        payload['duration'] = searchParameters.duration;
        payload['startdate'] = parseToApiDate(startDate);
      }

      if (productFilter.spotlength) {
        payload['spotlength'] = productFilter.spotlength;
      } else {
        payload['spotlength'] = searchParameters.spotLength;
      }

      if (productFilter.timeslots?.length > 0) {
        payload['timeslot'] = productFilter.timeslots[0];
      } else {
        payload['timeslot'] = searchParameters.timeslot;
      }
      actions.getPriceForProduct(payload).then((result) => {
        uiActions.setLoading('productRow', false);
        uiActions.setIsShowBottomBar(false);
        actions.setProductFilterData(productUrn, {
          frequency: undefined,
          budget: undefined,
          isBudgetChanged: false,
        });

        if (result?.data?.producturn) {
          actions.batchDeselectProduct(result?.data?.producturn);
        }
      });
    }
  };

  const generatePostPayload = (mapPotentialCoordinates?) => {
    const result = {
      sort: [
        {
          column: searchParameters.currentSort,
          direction: searchParameters.sort[searchParameters.currentSort],
        },
      ],
      group: ['format', 'subformat'],
      criteria: generateCriteria(searchLocations, searchParameters),
    };
    if (mapPotentialCoordinates) {
      result['mapview'] = mapPotentialCoordinates;
    }
    return result;
  };

  const fetchProducts = () => {
    uiActions.setLoading('productRow', true);
    actions.postSelect(generatePostPayload()).then((res) => {
      if (res === 'Aborting') {
        // That means some other fetch is in progress
        return;
      }
      uiActions.setLoading('productRow', false);
      uiActions.setIsShowBottomBar(false);
    });
  };
  return (
    isShowBottomBar && (
      <div className='footer w-100 bg-red p-5 bg-gray position-absolute bottom-0 d-flex justify-content-center align-items-center'>
        <Button
          className='ml-3 bg-white font-primary text-primary'
          buttonType={EButtonType.PillMedium}
          isDisabled={!searchLocations}
          onClick={() => {
            fetchProducts();
          }}
        >
          Apply search
        </Button>
        <Button
          className='ml-3 bg-white text-primary'
          buttonType={EButtonType.PillMedium}
          isDisabled={!productFiltersByUrn}
          onClick={() => {
            uiActions.setLoading('productRow', true);
            updateProductPrices();
          }}
        >
          Apply filters
        </Button>
        <Button
          className='ml-3 bg-white text-primary'
          buttonType={EButtonType.PillMedium}
          isDisabled={!productFiltersByUrn}
          onClick={() => {
            actions.clearProductFilterData();
            uiActions.setLoading('productRow', false);
            uiActions.setIsShowBottomBar(false);
          }}
        >
          Reset filters
        </Button>
      </div>
    )
  );
};

function mapStateToProps(state: AppState, ownProps: any) {
  return {
    ...ownProps,
    ...state.products,
    ...state.locations,
    ...state.ui,
    notices: state.products.notices,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(productsActions as any, dispatch),
    uiActions: bindActionCreators(UiActions as any, dispatch),
    dispatch: dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
